import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Save, Send, Download, X, Copy, ExternalLink, PenTool, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { API_BASE } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import SignaturePad from '../../components/SignaturePad';
import { useAuth } from '../../contexts/AuthContext';
import { StatusChip } from './NDAsList';

const EDITABLE = ['draft', 'issued', 'viewed'];

export default function NDADetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const canEdit   = can('proposals', 'edit');
    const canDelete = can('proposals', 'delete');

    const [nda, setNda]       = useState(null);
    const [form, setForm]     = useState(null);
    const [bodyMd, setBodyMd] = useState('');
    const [dirty, setDirty]   = useState(false);
    const [busy, setBusy]     = useState(false);
    const [showIssue, setShowIssue]               = useState(false);
    const [showCounterSign, setShowCounterSign]   = useState(false);
    const dirtyRef = useRef(false); dirtyRef.current = dirty;

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/ndas/${id}`);
            setNda(r.data);
            setBodyMd(r.data.body_md || '');
            setForm({
                title: r.data.title || '',
                recipient_name:         r.data.recipient_name || '',
                recipient_company:      r.data.recipient_company || '',
                recipient_email:        r.data.recipient_email || '',
                recipient_address:      r.data.recipient_address || '',
                effective_date:         r.data.effective_date || '',
                purpose:                r.data.purpose || '',
                client_project_name:    r.data.client_project_name || '',
                duration:               r.data.duration || '',
                confidentiality_period: r.data.confidentiality_period || '',
                special_terms:          r.data.special_terms || '',
                expires_in_days:        r.data.expires_in_days || 30,
            });
            setDirty(false);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to load NDA');
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };
    const editBody = (v) => { setBodyMd(v); setDirty(true); };

    const save = async () => {
        if (!form) return;
        setBusy(true);
        try {
            await api.patch(`/ndas/${id}`, { ...form, body_md: bodyMd });
            toast.success('Saved');
            setDirty(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const copyLink = () => {
        if (!nda?.public_url) return;
        navigator.clipboard.writeText(nda.public_url);
        toast.success('Link copied');
    };

    const cancel = async () => {
        if (!window.confirm('Cancel this NDA? It will be marked as cancelled and the public link will stop working.')) return;
        try {
            await api.post(`/ndas/${id}/cancel`);
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Cancel failed');
        }
    };

    if (!nda || !form) {
        return <div className="p-8 text-sm text-slate-500">Loading…</div>;
    }

    const editable = canEdit && EDITABLE.includes(nda.status);
    const canIssue = canEdit && ['draft', 'issued', 'viewed'].includes(nda.status);
    const canCounter = canEdit && nda.status === 'signed_by_recipient';

    return (
        <div data-testid="nda-detail">
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-2">
                        <Link to="/ndas" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
                        {nda.nda_number}
                        <StatusChip status={nda.status} />
                    </span>
                }
                description={`Recipient: ${nda.recipient_name} · ${nda.recipient_email}`}
                actions={
                    <div className="flex items-center gap-2">
                        {editable && (
                            <Button variant="outline" size="sm" onClick={save} disabled={busy || !dirty} data-testid="nda-save-btn">
                                <Save className="w-4 h-4 mr-1" /> {dirty ? 'Save' : 'Saved'}
                            </Button>
                        )}
                        {canIssue && (
                            <Button size="sm" onClick={() => setShowIssue(true)} data-testid="nda-issue-btn">
                                <Send className="w-4 h-4 mr-1" /> Issue
                            </Button>
                        )}
                        {canCounter && (
                            <Button size="sm" onClick={() => setShowCounterSign(true)} data-testid="nda-countersign-btn">
                                <PenTool className="w-4 h-4 mr-1" /> Counter-sign
                            </Button>
                        )}
                        {nda.status === 'signed' && (
                            <Button
                                size="sm"
                                onClick={() => window.open(`${API_BASE}/ndas/${id}/pdf`, '_blank')}
                                data-testid="nda-pdf-btn"
                            >
                                <Download className="w-4 h-4 mr-1" /> PDF
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="grid grid-cols-3 gap-6 px-8 py-6">
                {/* Left: form */}
                <div className="col-span-1 space-y-5">
                    <Card title="Recipient">
                        <FieldRow label="Name *">
                            <Input value={form.recipient_name} onChange={(e) => setField('recipient_name', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Email *">
                            <Input type="email" value={form.recipient_email} onChange={(e) => setField('recipient_email', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Company">
                            <Input value={form.recipient_company} onChange={(e) => setField('recipient_company', e.target.value)} disabled={!editable} placeholder="(blank = recipient fills)" />
                        </FieldRow>
                        <FieldRow label="Address">
                            <Input value={form.recipient_address} onChange={(e) => setField('recipient_address', e.target.value)} disabled={!editable} placeholder="(blank = recipient fills)" />
                        </FieldRow>
                    </Card>

                    <Card title="Terms">
                        <FieldRow label="Effective date">
                            <Input type="date" value={form.effective_date || ''} onChange={(e) => setField('effective_date', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Client / project">
                            <Input value={form.client_project_name} onChange={(e) => setField('client_project_name', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Duration">
                            <Input value={form.duration} onChange={(e) => setField('duration', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Confidentiality period">
                            <Input value={form.confidentiality_period} onChange={(e) => setField('confidentiality_period', e.target.value)} disabled={!editable} />
                        </FieldRow>
                        <FieldRow label="Purpose">
                            <textarea value={form.purpose} onChange={(e) => setField('purpose', e.target.value)} disabled={!editable} rows={3} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm disabled:bg-slate-50" />
                        </FieldRow>
                        <FieldRow label="Special terms">
                            <textarea value={form.special_terms} onChange={(e) => setField('special_terms', e.target.value)} disabled={!editable} rows={3} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm disabled:bg-slate-50" />
                        </FieldRow>
                        <FieldRow label="Expires in (days)">
                            <Input type="number" min="1" max="365" value={form.expires_in_days} onChange={(e) => setField('expires_in_days', Number(e.target.value) || 30)} disabled={!editable} />
                        </FieldRow>
                    </Card>

                    <Card title="Public link">
                        <div className="text-[12px] text-slate-600 break-all">{nda.public_url}</div>
                        <div className="flex gap-2 mt-2">
                            <Button variant="outline" size="sm" onClick={copyLink} className="h-8" data-testid="nda-copy-link-btn">
                                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.open(nda.public_url, '_blank')} className="h-8">
                                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open
                            </Button>
                        </div>
                    </Card>

                    <Card title="Signatures">
                        <SigBlock label="Recipient" sig={nda.recipient_signature} at={nda.recipient_signed_at} />
                        <SigBlock label="Beyond Measure (counter-sign)" sig={nda.counter_signature} at={nda.counter_signed_at} />
                    </Card>

                    {canDelete && nda.status !== 'signed' && (
                        <Button variant="outline" className="w-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={cancel}>
                            <Ban className="w-4 h-4 mr-1" /> Cancel NDA
                        </Button>
                    )}
                </div>

                {/* Right: rich body + live preview */}
                <div className="col-span-2 space-y-5">
                    <Card title="Document body" subtitle="Markdown supported. Placeholders like {recipient_name} are auto-filled in the preview.">
                        <textarea
                            value={bodyMd}
                            onChange={(e) => editBody(e.target.value)}
                            disabled={!editable}
                            rows={18}
                            className="w-full font-mono text-[12px] rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-50"
                            data-testid="nda-body-md"
                        />
                    </Card>
                    <Card title="Live preview">
                        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[13px] text-slate-800">
                            <PreviewBody body={nda.rendered_preview} />
                        </div>
                    </Card>
                </div>
            </div>

            {showIssue && <IssueModal nda={nda} onClose={() => setShowIssue(false)} onIssued={load} />}
            {showCounterSign && <CounterSignModal nda={nda} onClose={() => setShowCounterSign(false)} onSigned={load} />}
        </div>
    );
}


function Card({ title, subtitle, children }) {
    return (
        <div className="bg-white border border-[color:var(--bm-border)] rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{title}</div>
            {subtitle && <div className="text-[11.5px] text-slate-500 mb-3 -mt-0.5">{subtitle}</div>}
            <div className="space-y-2.5">{children}</div>
        </div>
    );
}

function FieldRow({ label, children }) {
    return (
        <div>
            <Label className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

function PreviewBody({ body }) {
    if (!body) return <div className="text-slate-400 italic">Preview will appear here.</div>;
    // Minimal markdown rendering: headings + bold
    const lines = body.split('\n');
    return (
        <>
            {lines.map((line, i) => {
                if (line.startsWith('# ')) return <h2 key={i} className="text-[18px] font-bold text-[color:var(--bm-primary)] mt-3 mb-1.5">{line.slice(2)}</h2>;
                if (line.startsWith('## ')) return <h3 key={i} className="text-[14px] font-semibold mt-3 mb-1 text-slate-800">{line.slice(3)}</h3>;
                if (line.startsWith('* ')) return <li key={i} className="ml-5 list-disc text-[13px]">{inline(line.slice(2))}</li>;
                if (!line.trim()) return <div key={i} className="h-2" />;
                return <p key={i} className="mb-1.5 text-[13px] leading-snug">{inline(line)}</p>;
            })}
        </>
    );
}

function inline(text) {
    // Split **bold**
    const parts = text.split(/(\*\*[^*]+\*\*)/);
    return parts.map((p, i) => (
        p.startsWith('**') && p.endsWith('**')
            ? <strong key={i}>{p.slice(2, -2)}</strong>
            : <React.Fragment key={i}>{p}</React.Fragment>
    ));
}

function SigBlock({ label, sig, at }) {
    return (
        <div className="border border-slate-200 rounded p-2.5">
            <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{label}</div>
            {!sig ? <div className="text-[12px] text-slate-400 italic">Not yet signed</div> : (
                <>
                    {sig.image_b64 && <img src={sig.image_b64.startsWith('data:') ? sig.image_b64 : `data:image/png;base64,${sig.image_b64}`} alt="signature" className="max-h-12 mb-1" />}
                    <div className="text-[13px] font-semibold">{sig.typed_name}</div>
                    {sig.title && <div className="text-[11.5px] text-slate-600">{sig.title}</div>}
                    <div className="text-[10.5px] text-slate-500 mt-0.5">{fmtTs(at)}</div>
                    {sig.ip && <div className="text-[10.5px] text-slate-400">IP {sig.ip}</div>}
                </>
            )}
        </div>
    );
}


function IssueModal({ nda, onClose, onIssued }) {
    const [to, setTo]   = useState(nda.recipient_email);
    const [cc, setCc]   = useState('');
    const [msg, setMsg] = useState('Please review and digitally sign before we proceed.');
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                to_email: to.trim() || nda.recipient_email,
                cc: cc.split(',').map(s => s.trim()).filter(Boolean),
                message: msg.trim(),
            };
            const r = await api.post(`/ndas/${nda.id}/issue`, payload);
            if (r.data.sent) toast.success('NDA emailed to recipient');
            else toast.warning(`Status updated, but email could not be sent: ${r.data.error || 'unknown'}. Use the public link.`);
            onIssued();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Issue failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogTitle>Issue NDA</DialogTitle>
                <DialogDescription>Send the public signing link to the recipient via your Microsoft mailbox.</DialogDescription>
                <div className="space-y-3 mt-3">
                    <FieldRow label="To"><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="issue-to" /></FieldRow>
                    <FieldRow label="CC (comma separated)"><Input value={cc} onChange={(e) => setCc(e.target.value)} /></FieldRow>
                    <FieldRow label="Message">
                        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
                    </FieldRow>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="issue-submit">
                        <Send className="w-4 h-4 mr-1" /> {busy ? 'Sending…' : 'Send'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function CounterSignModal({ nda, onClose, onSigned }) {
    const { user } = useAuth();
    const [typed, setTyped] = useState(user?.name || 'Fabio Dalla Venezia');
    const [title, setTitle] = useState('Director');
    const [imageB64, setImageB64] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!typed.trim()) return toast.error('Please type your signature.');
        setBusy(true);
        try {
            await api.post(`/ndas/${nda.id}/counter-sign`, {
                typed_signature: typed.trim(),
                title: title.trim(),
                signature_image_base64: imageB64,
            });
            toast.success('NDA counter-signed and PDF generated');
            onSigned();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Counter-sign failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogTitle>Counter-sign NDA</DialogTitle>
                <DialogDescription>
                    The recipient signed on {fmtTs(nda.recipient_signed_at)}. Sign to fully execute.
                </DialogDescription>
                <div className="space-y-3 mt-3">
                    <FieldRow label="Your name"><Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="counter-typed" /></FieldRow>
                    <FieldRow label="Your title"><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="counter-title" /></FieldRow>
                    <FieldRow label="Signature (optional)">
                        <SignaturePad
                            onDrawnChange={setImageB64}
                            onTypedChange={setTyped}
                            typedValue={typed}
                        />
                    </FieldRow>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="counter-submit">
                        <PenTool className="w-4 h-4 mr-1" /> {busy ? 'Signing…' : 'Counter-sign'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}


function fmtTs(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
}
